import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { useSubscriptionStatus } from "@/hooks/use-payments";
import { trialBannerCopy } from "@/lib/billingCopy";

const DISMISS_KEY = "fomo-fi-trial-banner-dismissed";

/**
 * Free-trial banner (T5). Renders only while the subscription is `trialing`,
 * shows days remaining, links to the subscribe/manage page, and is
 * dismiss-per-session (sessionStorage). Hidden once the user is paid or the
 * status query hasn't resolved.
 */
export default function TrialBanner() {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === "true";
    } catch {
      return false;
    }
  });

  const { data } = useSubscriptionStatus();

  if (dismissed || data?.status !== "trialing") return null;

  function dismiss() {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "true");
    } catch {
      // ignore storage failures — banner just reappears next load
    }
  }

  return (
    <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-border bg-primary/5 px-4 py-2.5">
      <div className="flex items-center gap-2 text-sm">
        <Sparkles className="h-4 w-4 shrink-0 text-primary" />
        <span className="text-foreground">
          {trialBannerCopy(data.trial_end)}{" "}
          <Link to="/payment" className="font-medium text-primary hover:underline">
            Manage subscription
          </Link>
        </span>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss trial banner"
        className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
